import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserSettingsContext } from '@/hooks/useUserSettingsContext';
import CustomDropdown from '@/components/CustomDropdown';
import { buildUserTimezoneOptions, getBrowserTimezone } from '@/utils/timezoneOptions';
import { WEEK_START_OPTIONS, detectWeekStartFromLocale, getWeekStartDayLabel } from '@/utils/weekSettings';
import { getCurrencyOptions, getCurrencyByCode } from '@/utils/currency';

export default function Settings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const {
    holidaySettings,
    displaySettings,
    timezoneSettings,
    localeSettings,
    projectSettings,
    effectiveWeekStartDay,
    countries,
    states,
    loading,
    error,
    updateHolidaySettings,
    updateDisplaySettings,
    updateTimezoneSettings,
    updateLocaleSettings,
    updateProjectSettings,
    updateLocalSettings,
    updateLocalDisplaySettings,
    updateLocalTimezoneSettings,
    updateLocalLocaleSettings,
    setError,
  } = useUserSettingsContext();

  // Build timezone options
  const browserTimezone = getBrowserTimezone();
  const timezoneOptions = useMemo(() => {
    return buildUserTimezoneOptions(browserTimezone);
  }, [browserTimezone]);

  // Debounce timer ref for color picker
  const colorDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const overlapColorDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const budgetColorDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (colorDebounceRef.current) {
        clearTimeout(colorDebounceRef.current);
      }
      if (overlapColorDebounceRef.current) {
        clearTimeout(overlapColorDebounceRef.current);
      }
      if (budgetColorDebounceRef.current) {
        clearTimeout(budgetColorDebounceRef.current);
      }
    };
  }, []);

  // Auto-save country selection
  const handleCountryChange = useCallback((values: string[]) => {
    const newCountry = values[0] || '';
    updateLocalSettings({ country: newCountry, state: undefined }); // Clear state when country changes
    updateHolidaySettings({ country: newCountry, state: undefined });
  }, [updateLocalSettings, updateHolidaySettings]);

  // Auto-save state selection
  const handleStateChange = useCallback((values: string[]) => {
    const newState = values[0] || undefined;
    updateLocalSettings({ state: newState });
    updateHolidaySettings({ state: newState });
  }, [updateLocalSettings, updateHolidaySettings]);

  // Auto-save color with debounce
  const handleColorChange = useCallback((color: string) => {
    updateLocalSettings({ holidayColor: color });

    // Clear existing timer
    if (colorDebounceRef.current) {
      clearTimeout(colorDebounceRef.current);
    }

    // Set new debounced save
    colorDebounceRef.current = setTimeout(() => {
      updateHolidaySettings({ holidayColor: color });
    }, 500);
  }, [updateLocalSettings, updateHolidaySettings]);

  // Auto-save show holidays toggle
  const handleShowHolidaysChange = useCallback(() => {
    const previousValue = holidaySettings.showHolidays;
    const newValue = !previousValue;
    updateLocalSettings({ showHolidays: newValue });
    updateHolidaySettings({ showHolidays: newValue }).catch(() => {
      // Rollback on failure
      updateLocalSettings({ showHolidays: previousValue });
    });
  }, [holidaySettings.showHolidays, updateLocalSettings, updateHolidaySettings]);

  // Auto-save timezone indicator toggle with rollback on error
  const handleTimezoneIndicatorChange = useCallback(() => {
    const previousValue = displaySettings.showTimezoneIndicator;
    const newValue = !previousValue;
    updateLocalDisplaySettings({ showTimezoneIndicator: newValue });
    updateDisplaySettings({ showTimezoneIndicator: newValue }).catch(() => {
      // Rollback on failure
      updateLocalDisplaySettings({ showTimezoneIndicator: previousValue });
    });
  }, [displaySettings.showTimezoneIndicator, updateLocalDisplaySettings, updateDisplaySettings]);

  // Auto-save overlap indicator toggle with rollback on error
  const handleOverlapIndicatorChange = useCallback(() => {
    const previousValue = displaySettings.showOverlapIndicator;
    const newValue = !previousValue;
    updateLocalDisplaySettings({ showOverlapIndicator: newValue });
    updateDisplaySettings({ showOverlapIndicator: newValue }).catch(() => {
      // Rollback on failure
      updateLocalDisplaySettings({ showOverlapIndicator: previousValue });
    });
  }, [displaySettings.showOverlapIndicator, updateLocalDisplaySettings, updateDisplaySettings]);

  // Auto-save active entry animations toggle with rollback on error
  const handleActiveEntryAnimationsChange = useCallback(() => {
    const previousValue = displaySettings.showActiveEntryAnimations;
    const newValue = !previousValue;
    updateLocalDisplaySettings({ showActiveEntryAnimations: newValue });
    updateDisplaySettings({ showActiveEntryAnimations: newValue }).catch(() => {
      // Rollback on failure
      updateLocalDisplaySettings({ showActiveEntryAnimations: previousValue });
    });
  }, [displaySettings.showActiveEntryAnimations, updateLocalDisplaySettings, updateDisplaySettings]);

  // Auto-save budget warnings toggle with rollback on error
  const handleBudgetWarningsChange = useCallback(() => {
    const previousValue = displaySettings.showBudgetWarnings;
    const newValue = !previousValue;
    updateLocalDisplaySettings({ showBudgetWarnings: newValue });
    updateDisplaySettings({ showBudgetWarnings: newValue }).catch(() => {
      // Rollback on failure
      updateLocalDisplaySettings({ showBudgetWarnings: previousValue });
    });
  }, [displaySettings.showBudgetWarnings, updateLocalDisplaySettings, updateDisplaySettings]);

  // Auto-save overlap indicator color with debounce
  const handleOverlapColorChange = useCallback((color: string) => {
    updateLocalDisplaySettings({ overlapIndicatorColor: color });
    if (overlapColorDebounceRef.current) clearTimeout(overlapColorDebounceRef.current);
    overlapColorDebounceRef.current = setTimeout(() => {
      updateDisplaySettings({ overlapIndicatorColor: color });
    }, 500);
  }, [updateLocalDisplaySettings, updateDisplaySettings]);

  // Auto-save budget warning color with debounce
  const handleBudgetColorChange = useCallback((color: string) => {
    updateLocalDisplaySettings({ budgetWarningColor: color });
    if (budgetColorDebounceRef.current) clearTimeout(budgetColorDebounceRef.current);
    budgetColorDebounceRef.current = setTimeout(() => {
      updateDisplaySettings({ budgetWarningColor: color });
    }, 500);
  }, [updateLocalDisplaySettings, updateDisplaySettings]);

  // Auto-save timezone preference with rollback on error
  const handleTimezoneChange = useCallback((values: string[]) => {
    const previousValue = timezoneSettings.preferredTimezone;
    // Empty string means use browser default (stored as null)
    const newTimezone = values[0] === '' ? null : (values[0] || null);
    updateLocalTimezoneSettings({ preferredTimezone: newTimezone });
    updateTimezoneSettings({ preferredTimezone: newTimezone }).catch(() => {
      // Rollback on failure
      updateLocalTimezoneSettings({ preferredTimezone: previousValue });
    });
  }, [timezoneSettings.preferredTimezone, updateLocalTimezoneSettings, updateTimezoneSettings]);

  // Auto-save week start preference with rollback on error
  const handleWeekStartChange = useCallback((values: string[]) => {
    const previousValue = localeSettings.weekStartsOn;
    const value = values[0];
    // Convert string value to correct type
    const weekStartsOn = value === 'auto' ? 'auto' : (value === '0' ? 0 : 1);
    updateLocalLocaleSettings({ weekStartsOn });
    updateLocaleSettings({ weekStartsOn }).catch(() => {
      // Rollback on failure
      updateLocalLocaleSettings({ weekStartsOn: previousValue });
    });
  }, [localeSettings.weekStartsOn, updateLocalLocaleSettings, updateLocaleSettings]);

  // Auto-save currency preference with rollback on error
  const handleCurrencyChange = useCallback((values: string[]) => {
    const previousValue = displaySettings.defaultCurrency;
    const newCurrency = values[0] || 'EUR';
    updateLocalDisplaySettings({ defaultCurrency: newCurrency });
    updateDisplaySettings({ defaultCurrency: newCurrency }).catch(() => {
      updateLocalDisplaySettings({ defaultCurrency: previousValue });
    });
  }, [displaySettings.defaultCurrency, updateLocalDisplaySettings, updateDisplaySettings]);

  if (authLoading || !user) {
    return null;
  }

  return (
    <div className="space-y-6">

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="inline-flex text-red-400 hover:text-red-600"
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Holiday Settings Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">Holiday settings</p>
          {loading && (
            <div className="flex items-center text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </div>
          )}
        </div>

        {/* Show Holidays Checkbox */}
        <div
          className={`flex items-center justify-between p-3 rounded-md transition-all duration-200 mb-6 ${
            holidaySettings.showHolidays
              ? 'bg-blue-50'
              : 'bg-gray-50/50 hover:bg-gray-100/50'
          }`}
        >
          <label className="flex items-center cursor-pointer flex-1" htmlFor="showHolidays">
            <input
              type="checkbox"
              id="showHolidays"
              checked={holidaySettings.showHolidays}
              onChange={handleShowHolidaysChange}
              className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
            />
            <div>
              <span className={`text-sm ${holidaySettings.showHolidays ? 'text-blue-800 font-semibold' : 'text-gray-900'}`}>
                Show Holidays
              </span>
              <p className={`text-sm ${holidaySettings.showHolidays ? 'text-blue-600' : 'text-gray-500'}`}>
                Display holidays in calendar and reports
              </p>
            </div>
          </label>
          <input
            type="color"
            id="holidayColor"
            value={holidaySettings.holidayColor}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-8 h-8 border border-gray-300 rounded cursor-pointer ml-3 flex-shrink-0"
            title="Calendar Holiday Color"
          />
        </div>

        {/* Country and State Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Country Selection */}
          <CustomDropdown
            id="country"
            label="Country"
            placeholder="Select a country"
            options={countries.map(country => ({ value: country.code, label: country.name }))}
            selectedValues={holidaySettings.country ? [holidaySettings.country] : []}
            onSelectionChange={handleCountryChange}
            multiSelect={false}
          />

          {/* State/Province Selection */}
          <CustomDropdown
            id="state"
            label="State/Province (Optional)"
            placeholder="Select a state/province"
            options={states.map(state => ({ value: state.code, label: state.name }))}
            selectedValues={holidaySettings.state ? [holidaySettings.state] : []}
            onSelectionChange={handleStateChange}
            multiSelect={false}
            disabled={states.length === 0}
          />
        </div>

      </div>

      {/* Display Settings Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <p className="text-gray-600 mb-6">Display settings</p>

        <div className="space-y-6">
          {/* Show Active Entry Animations Checkbox */}
          <label
            className={`flex items-center p-3 rounded-md cursor-pointer transition-all duration-200 ${
              displaySettings.showActiveEntryAnimations
                ? 'bg-blue-50'
                : 'bg-gray-50/50 hover:bg-gray-100/50'
            }`}
          >
            <input
              type="checkbox"
              id="showActiveEntryAnimations"
              checked={displaySettings.showActiveEntryAnimations}
              onChange={handleActiveEntryAnimationsChange}
              className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
            />
            <div>
              <span className={`text-sm ${displaySettings.showActiveEntryAnimations ? 'text-blue-800 font-semibold' : 'text-gray-900'}`}>
                Active Entry Animations
              </span>
              <p className={`text-sm ${displaySettings.showActiveEntryAnimations ? 'text-blue-600' : 'text-gray-500'}`}>
                Show pulsing effects in the header and time tracker when a time entry is running
              </p>
            </div>
          </label>

          {/* Show Overlap Indicator Checkbox */}
          <div
            className={`flex items-center justify-between p-3 rounded-md transition-all duration-200 ${
              displaySettings.showOverlapIndicator
                ? 'bg-blue-50'
                : 'bg-gray-50/50 hover:bg-gray-100/50'
            }`}
          >
            <label className="flex items-center cursor-pointer flex-1" htmlFor="showOverlapIndicator">
              <input
                type="checkbox"
                id="showOverlapIndicator"
                checked={displaySettings.showOverlapIndicator}
                onChange={handleOverlapIndicatorChange}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
              />
              <div>
                <span className={`text-sm ${displaySettings.showOverlapIndicator ? 'text-blue-800 font-semibold' : 'text-gray-900'}`}>
                  Show Overlap Indicator
                </span>
                <p className={`text-sm ${displaySettings.showOverlapIndicator ? 'text-blue-600' : 'text-gray-500'}`}>
                  Display a warning indicator when time entries overlap within the same project
                </p>
              </div>
            </label>
            {displaySettings.showOverlapIndicator && (
              <input
                type="color"
                value={displaySettings.overlapIndicatorColor}
                onChange={(e) => handleOverlapColorChange(e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer ml-3 flex-shrink-0"
                title="Overlap Highlight Color"
              />
            )}
          </div>

          {/* Show Budget Warnings Checkbox */}
          <div
            className={`flex items-center justify-between p-3 rounded-md transition-all duration-200 ${
              displaySettings.showBudgetWarnings
                ? 'bg-blue-50'
                : 'bg-gray-50/50 hover:bg-gray-100/50'
            }`}
          >
            <label className="flex items-center cursor-pointer flex-1" htmlFor="showBudgetWarnings">
              <input
                type="checkbox"
                id="showBudgetWarnings"
                checked={displaySettings.showBudgetWarnings}
                onChange={handleBudgetWarningsChange}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
              />
              <div>
                <span className={`text-sm ${displaySettings.showBudgetWarnings ? 'text-blue-800 font-semibold' : 'text-gray-900'}`}>
                  Show Budget Warnings
                </span>
                <p className={`text-sm ${displaySettings.showBudgetWarnings ? 'text-blue-600' : 'text-gray-500'}`}>
                  Highlight rows that exceed the project budget in reporting and on the dashboard
                </p>
              </div>
            </label>
            {displaySettings.showBudgetWarnings && (
              <input
                type="color"
                value={displaySettings.budgetWarningColor}
                onChange={(e) => handleBudgetColorChange(e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer ml-3 flex-shrink-0"
                title="Budget Warning Color"
              />
            )}
          </div>

          {/* Show Timezone Indicator Checkbox */}
          <label
            className={`flex items-center p-3 rounded-md cursor-pointer transition-all duration-200 ${
              displaySettings.showTimezoneIndicator
                ? 'bg-blue-50'
                : 'bg-gray-50/50 hover:bg-gray-100/50'
            }`}
          >
            <input
              type="checkbox"
              id="showTimezoneIndicator"
              checked={displaySettings.showTimezoneIndicator}
              onChange={handleTimezoneIndicatorChange}
              className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
            />
            <div>
              <span className={`text-sm ${displaySettings.showTimezoneIndicator ? 'text-blue-800 font-semibold' : 'text-gray-900'}`}>
                Show Timezone Indicator
              </span>
              <p className={`text-sm ${displaySettings.showTimezoneIndicator ? 'text-blue-600' : 'text-gray-500'}`}>
                Display an indicator when time entries were recorded in a different timezone
              </p>
            </div>
          </label>

          {/* Default Currency */}
          <div
            className={`p-3 rounded-md transition-all duration-200 ${
              displaySettings.defaultCurrency !== 'EUR'
                ? 'bg-blue-50'
                : 'bg-gray-50/50 hover:bg-gray-100/50'
            }`}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                id="useCustomCurrency"
                checked={displaySettings.defaultCurrency !== 'EUR'}
                onChange={() => {
                  if (displaySettings.defaultCurrency !== 'EUR') {
                    handleCurrencyChange(['EUR']);
                  }
                }}
                readOnly={displaySettings.defaultCurrency === 'EUR'}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
              />
              <div className="flex-1 grid grid-cols-2 gap-4 items-center">
                <div>
                  <div className={`text-sm ${
                    displaySettings.defaultCurrency !== 'EUR'
                      ? 'text-blue-800 font-semibold'
                      : 'text-gray-900'
                  }`}>
                    Default Currency
                  </div>
                  <div className={`text-sm ${
                    displaySettings.defaultCurrency !== 'EUR'
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}>
                    {displaySettings.defaultCurrency !== 'EUR'
                      ? `Using ${getCurrencyByCode(displaySettings.defaultCurrency).name} (${getCurrencyByCode(displaySettings.defaultCurrency).symbol})`
                      : `Default currency for projects: Euro (€)`
                    }
                  </div>
                </div>
                <div>
                  <CustomDropdown
                    id="defaultCurrency"
                    label=""
                    placeholder="Select a currency"
                    options={getCurrencyOptions()}
                    selectedValues={[displaySettings.defaultCurrency]}
                    onSelectionChange={handleCurrencyChange}
                    multiSelect={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Week Starts On */}
          <div
            className={`p-3 rounded-md transition-all duration-200 ${
              localeSettings.weekStartsOn !== 'auto'
                ? 'bg-blue-50'
                : 'bg-gray-50/50 hover:bg-gray-100/50'
            }`}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                id="useCustomWeekStart"
                checked={localeSettings.weekStartsOn !== 'auto'}
                onChange={() => {
                  if (localeSettings.weekStartsOn !== 'auto') {
                    // Uncheck: reset to auto
                    handleWeekStartChange(['auto']);
                  }
                }}
                readOnly={localeSettings.weekStartsOn === 'auto'}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
              />
              <div className="flex-1 grid grid-cols-2 gap-4 items-center">
                <div>
                  <div className={`text-sm ${
                    localeSettings.weekStartsOn !== 'auto'
                      ? 'text-blue-800 font-semibold'
                      : 'text-gray-900'
                  }`}>
                    Week Starts On
                  </div>
                  <div className={`text-sm ${
                    localeSettings.weekStartsOn !== 'auto'
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}>
                    {localeSettings.weekStartsOn !== 'auto'
                      ? `Weeks start on ${getWeekStartDayLabel(effectiveWeekStartDay)}`
                      : `Auto-detected from locale: ${getWeekStartDayLabel(detectWeekStartFromLocale())}`
                    }
                  </div>
                </div>
                <div>
                  <CustomDropdown
                    id="weekStartsOn"
                    label=""
                    placeholder="Select week start"
                    options={WEEK_START_OPTIONS.map(opt => ({ value: String(opt.value), label: opt.label }))}
                    selectedValues={[String(localeSettings.weekStartsOn)]}
                    onSelectionChange={handleWeekStartChange}
                    multiSelect={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Timezone Settings Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <p className="text-gray-600 mb-6">Timezone settings</p>

        <div className="space-y-4">
          {/* Preferred Timezone */}
          <div
            className={`p-3 rounded-md transition-all duration-200 ${
              timezoneSettings.preferredTimezone && timezoneSettings.preferredTimezone !== browserTimezone
                ? 'bg-blue-50'
                : 'bg-gray-50/50 hover:bg-gray-100/50'
            }`}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                id="usePreferredTimezone"
                checked={!!(timezoneSettings.preferredTimezone && timezoneSettings.preferredTimezone !== browserTimezone)}
                onChange={() => {
                  if (timezoneSettings.preferredTimezone && timezoneSettings.preferredTimezone !== browserTimezone) {
                    // Uncheck: reset to browser timezone (null)
                    handleTimezoneChange(['']);
                  }
                }}
                readOnly={!(timezoneSettings.preferredTimezone && timezoneSettings.preferredTimezone !== browserTimezone)}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
              />
              <div className="flex-1 grid grid-cols-2 gap-4 items-center">
                <div>
                  <div className={`text-sm ${
                    timezoneSettings.preferredTimezone && timezoneSettings.preferredTimezone !== browserTimezone
                      ? 'text-blue-800 font-semibold'
                      : 'text-gray-900'
                  }`}>
                    Preferred Timezone for Time Tracking and Reporting
                  </div>
                  <div className={`text-sm ${
                    timezoneSettings.preferredTimezone && timezoneSettings.preferredTimezone !== browserTimezone
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}>
                    {timezoneSettings.preferredTimezone && timezoneSettings.preferredTimezone !== browserTimezone
                      ? `Time entries will be recorded in timezone: ${timezoneSettings.preferredTimezone.replace(/_/g, ' ')}`
                      : `Using browser default timezone: ${browserTimezone.replace(/_/g, ' ')}`
                    }
                  </div>
                </div>
                <div>
                  <CustomDropdown
                    id="preferredTimezone"
                    label=""
                    placeholder="Select a timezone"
                    options={timezoneOptions}
                    selectedValues={[timezoneSettings.preferredTimezone || '']}
                    onSelectionChange={handleTimezoneChange}
                    multiSelect={false}
                    searchable={true}
                    searchPlaceholder="Search timezones..."
                    showGroups={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <p className="text-gray-600 mb-6">Project settings</p>

        <div className="space-y-4">
          <div
            className={`p-3 rounded-md transition-all duration-200 ${
              projectSettings.autoArchiveMonths !== 12
                ? 'bg-blue-50'
                : 'bg-gray-50/50 hover:bg-gray-100/50'
            }`}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                id="useCustomAutoArchive"
                checked={projectSettings.autoArchiveMonths !== 12}
                onChange={() => {
                  if (projectSettings.autoArchiveMonths !== 12) {
                    updateProjectSettings({ autoArchiveMonths: 12 });
                  }
                }}
                readOnly={projectSettings.autoArchiveMonths === 12}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
              />
              <div className="flex-1 grid grid-cols-2 gap-4 items-center">
                <div>
                  <div className={`text-sm ${
                    projectSettings.autoArchiveMonths !== 12
                      ? 'text-blue-800 font-semibold'
                      : 'text-gray-900'
                  }`}>
                    Auto-archive inactive projects
                  </div>
                  <div className={`text-sm ${
                    projectSettings.autoArchiveMonths !== 12
                      ? 'text-blue-600'
                      : 'text-gray-500'
                  }`}>
                    {projectSettings.autoArchiveMonths !== 12
                      ? `Archiving projects with no entries in the last ${projectSettings.autoArchiveMonths / 12} years`
                      : 'Archive active projects with no entries in the last year'
                    }
                  </div>
                </div>
                <div>
                  <CustomDropdown
                    id="auto-archive-months"
                    label=""
                    placeholder="Select threshold"
                    options={[
                      { value: '12', label: '1 year' },
                      { value: '24', label: '2 years' },
                      { value: '36', label: '3 years' },
                    ]}
                    selectedValues={[String(projectSettings.autoArchiveMonths)]}
                    onSelectionChange={([v]) => updateProjectSettings({ autoArchiveMonths: parseInt(v) })}
                    multiSelect={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
